import { ChartBarIcon, Cog6ToothIcon, ServerIcon, UsersIcon } from '@heroicons/react/24/outline';
import { Button, ButtonGroup, Dropdown, DropdownTrigger, DropdownMenu, DropdownItem } from '@nextui-org/react';
import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

interface NavigationProps {
  user: string;
  isAdmin: boolean;
  isSuperAdmin: boolean;
}

export const NavigationUI = ({ user, isAdmin, isSuperAdmin }: NavigationProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; path: string; label: string } | null>(null);

  const isActive = (path: string) => location.pathname === path;

  const navigationItems = [
    {
      path: '/monitors',
      label: 'Monitor Server',
      icon: ChartBarIcon,
      showFor: ['admin', 'user'],
    },
    {
      path: '/servers',
      label: 'Manage Server',
      icon: ServerIcon,
      showFor: ['admin'],
    },
    {
      path: '/users',
      label: 'Users',
      icon: UsersIcon,
      showFor: ['admin'],
    },
    {
      path: '/manage',
      label: 'System Config',
      icon: Cog6ToothIcon,
      showFor: ['superAdmin'],
    },
  ];

  const visibleItems = navigationItems.filter(item => {
    if (item.showFor.includes('superAdmin') && isSuperAdmin) return true;
    if (item.showFor.includes('admin') && isAdmin) return true;
    if (item.showFor.includes('user')) return true;
    return false;
  });

  const handleButtonClick = (e: React.MouseEvent, path: string) => {
    // If Ctrl key is pressed, open in new tab
    if (e.ctrlKey) {
      window.open(path, '_blank');
    } else {
      // Normal left click - navigate in current tab
      navigate(path);
    }
  };

  const handleContextMenu = (e: React.MouseEvent, path: string, label: string) => {
    e.preventDefault();
    setContextMenu({
      x: e.clientX,
      y: e.clientY,
      path,
      label,
    });
  };

  const handleContextMenuAction = (action: 'current' | 'new') => {
    if (!contextMenu) return;

    if (action === 'new') {
      window.open(contextMenu.path, '_blank');
    } else {
      navigate(contextMenu.path);
    }
    setContextMenu(null);
  };

  const handleClickOutside = () => {
    setContextMenu(null);
  };

  React.useEffect(() => {
    if (contextMenu) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [contextMenu]);

  return (
    <>
      <ButtonGroup variant='bordered' size='sm'>
        {visibleItems.map(item => {
          const Icon = item.icon;
          return (
            <Button
              key={item.path}
              variant={isActive(item.path) ? 'solid' : 'bordered'}
              color={isActive(item.path) ? 'primary' : 'default'}
              isIconOnly
              onPress={e => handleButtonClick(e as any, item.path)}
              onMouseDown={e => {
                // Handle middle mouse button
                if (e.button === 1) {
                  e.stopPropagation();
                  window.open(item.path, '_blank');
                }
              }}
              onContextMenu={e => handleContextMenu(e, item.path, item.label)}
              className='w-8 h-8 min-w-8'
              title={`${item.label} (Right-click for options)`}
            >
              <Icon className='h-4 w-4' />
            </Button>
          );
        })}
      </ButtonGroup>

      {/* Custom Context Menu */}
      {contextMenu && (
        <div
          className='fixed z-50'
          style={{
            left: contextMenu.x,
            top: contextMenu.y,
          }}
        >
          <Dropdown isOpen={true} onOpenChange={() => setContextMenu(null)}>
            <DropdownTrigger>
              <div style={{ width: 0, height: 0, opacity: 0 }} />
            </DropdownTrigger>
            <DropdownMenu
              aria-label={`${contextMenu.label} options`}
              onAction={key => {
                if (key === 'open-in-new-tab') {
                  handleContextMenuAction('new');
                } else if (key === 'open-in-current-tab') {
                  handleContextMenuAction('current');
                }
              }}
              classNames={{
                base: 'bg-transparent',
                list: 'bg-transparent',
              }}
            >
              <DropdownItem
                key='open-in-current-tab'
                textValue='Open in Current Tab'
                className='bg-[#F9E79F] hover:bg-[#F7DC6F] hover:shadow-md transition-all duration-200 text-gray-800 rounded-lg m-0 data-[hover=true]:bg-[#F7DC6F] data-[hover=true]:shadow-md'
              >
                Open in Current Tab
              </DropdownItem>
              <DropdownItem
                key='open-in-new-tab'
                textValue='Open in New Tab'
                className='bg-[#F9E79F] hover:bg-[#F7DC6F] hover:shadow-md transition-all duration-200 text-gray-800 rounded-lg m-0 data-[hover=true]:bg-[#F7DC6F] data-[hover=true]:shadow-md'
              >
                Open in New Tab
              </DropdownItem>
            </DropdownMenu>
          </Dropdown>
        </div>
      )}
    </>
  );
};
